// Symptom & pain follow-up card (SYMPTOM_FOLLOWUP_PLAN.md §5, D-SF-1/3/5/9/10).
// One card per open symptom/headache; renders in a stack in ChatPane's proactive zone.
//
// Density (D-SF-R3, SYMPTOM_FOLLOWUP_REFINEMENT_PLAN.md): the patient picks "full" (a
// roomy single-control card) or "compact" (a one-line row). Both share the same
// severity logic and the de-duplicated 1–10 tap bar — the old redundant ▼/▲ stepper
// plus segment bar is gone.

import { useEffect, useRef, useState } from "react";
import {
  SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS,
  formatReminderInterval,
  type OpenSymptom,
  type SymptomCardDensity,
} from "@medassist/shared";
import { recordSymptomFollowUp, setSymptomReminderInterval, snoozeSymptomFollowUp, ApiError } from "../lib/api.js";

function relativeTime(isoString: string): string {
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "yesterday";
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  return `${diffWeeks}w ago`;
}

function todayStr() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, "0")}-${String(n.getDate()).padStart(2, "0")}`;
}

// ── Mini sparkline ─────────────────────────────────────────────────────────────

function Sparkline({ history }: { history: OpenSymptom["history"] }) {
  // history is newest-first; reverse for left→right chronological order
  const points = [...history].reverse().filter((p) => p.severity > 0);
  if (points.length < 2) return null;

  const W = 56, H = 18, pad = 2;
  const xs = points.map((_, i) => pad + (i / (points.length - 1)) * (W - pad * 2));
  const ys = points.map((p) => H - pad - ((p.severity - 1) / 9) * (H - pad * 2));
  const d = xs.map((x, i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${ys[i]!.toFixed(1)}`).join(" ");

  // Colour by trend: improving = green, worsening = amber, flat = gray
  const first = points[0]!.severity;
  const last = points[points.length - 1]!.severity;
  const stroke =
    last < first - 0.5
      ? "#22c55e" // improving
      : last > first + 0.5
      ? "#f59e0b" // worsening
      : "#94a3b8"; // stable

  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="shrink-0">
      <path d={d} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Severity tap bar (shared by both densities) ─────────────────────────────────

function SeverityBar({
  value,
  onPick,
  disabled,
  thin,
}: {
  value: number;
  onPick: (n: number) => void;
  disabled: boolean;
  thin?: boolean;
}) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
        <button
          key={n}
          onClick={() => onPick(n)}
          disabled={disabled}
          aria-label={`Severity ${n}`}
          className={
            (thin ? "h-1.5" : "h-2") +
            " flex-1 rounded-full transition-colors disabled:cursor-default " +
            (n <= value
              ? n <= 3
                ? "bg-green-400 dark:bg-green-500"
                : n <= 6
                ? "bg-yellow-400 dark:bg-yellow-500"
                : "bg-red-500 dark:bg-red-400"
              : "bg-rose-200 dark:bg-rose-800/60")
          }
        />
      ))}
    </div>
  );
}

// ── Card ───────────────────────────────────────────────────────────────────────

interface Props {
  patientId: string;
  symptom: OpenSymptom;
  density?: SymptomCardDensity;
  onDone: (key: string) => void;  // remove this card from the stack
  hideSnooze?: boolean;
  /** D-SF-I5: suppress the reminder-interval stepper (QuickSymptomLog reuse). */
  hideReminder?: boolean;
  /** Optional free-text note saved alongside the update (dashboard quick-log only). */
  note?: string;
  /** D-PAIN-4 (PAIN_TRACKING_PLAN Task 5.1/5.2): when set, the check-in is written
   *  against this pain site — the server copies its bodyMap forward so the Map tab
   *  keeps the location and the site does not fork. */
  siteId?: string;
}

export function SymptomFollowUpCard({
  patientId,
  symptom,
  density = "full",
  onDone,
  hideSnooze = false,
  hideReminder = false,
  note,
  siteId,
}: Props) {
  const [stagedSeverity, setStagedSeverity] = useState(symptom.currentSeverity);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeMode, setTimeMode] = useState<"now" | "custom">("now");
  const [customDate, setCustomDate] = useState(todayStr);
  const [customTime, setCustomTime] = useState(() => {
    const n = new Date();
    return `${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`;
  });

  // ── Reminder interval stepper (D-SF-I5/I6): full density only ──────────────
  const [intervalHours, setIntervalHours] = useState(symptom.reminderIntervalHours ?? 6);
  const lastSavedIntervalRef = useRef(intervalHours);
  const intervalDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (intervalDebounceRef.current) clearTimeout(intervalDebounceRef.current);
    };
  }, []);

  function stepInterval(direction: 1 | -1) {
    const options = SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS;
    const idx = options.indexOf(intervalHours as (typeof options)[number]);
    const nextIdx = Math.min(options.length - 1, Math.max(0, (idx === -1 ? 0 : idx) + direction));
    const next = options[nextIdx]!;
    if (next === intervalHours) return;
    setIntervalHours(next);
    setError(null);
    if (intervalDebounceRef.current) clearTimeout(intervalDebounceRef.current);
    intervalDebounceRef.current = setTimeout(() => {
      void setSymptomReminderInterval({ patientId, key: symptom.key, hours: next })
        .then(() => {
          lastSavedIntervalRef.current = next;
        })
        .catch((err) => {
          setIntervalHours(lastSavedIntervalRef.current);
          setError(err instanceof ApiError ? err.message : "Save failed.");
        });
    }, 600);
  }

  function selectCustomMode() {
    const n = new Date();
    setCustomTime(`${String(n.getHours()).padStart(2, "0")}:${String(n.getMinutes()).padStart(2, "0")}`);
    setCustomDate(todayStr());
    setTimeMode("custom");
  }

  const isDue = symptom.dueForCheckIn;

  async function update(severity: number, resolved = false) {
    setSaving(true);
    setError(null);
    try {
      let occurredAt: string;
      if (timeMode === "custom" && customTime) {
        const [year, month, day] = customDate.split("-").map(Number);
        const [h, m] = customTime.split(":").map(Number);
        const d = new Date(year!, month! - 1, day!, h ?? 0, m ?? 0, 0, 0);
        occurredAt = d.toISOString();
      } else {
        occurredAt = new Date().toISOString();
      }
      await recordSymptomFollowUp({
        patientId,
        key: symptom.key,
        eventType: symptom.eventType,
        severity,
        resolved: resolved || undefined,
        occurredAt,
        notes: note?.trim() || undefined,
        siteId,
      });
      onDone(symptom.key);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Save failed.");
      setSaving(false);
    }
  }

  async function snooze() {
    setSaving(true);
    setError(null);
    try {
      await snoozeSymptomFollowUp({ patientId, key: symptom.key });
      onDone(symptom.key);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Snooze failed.");
      setSaving(false);
    }
  }

  // Display name: capitalize first letter
  const displayName = symptom.name.charAt(0).toUpperCase() + symptom.name.slice(1);

  // Region chip label (region-insensitive identity means this is display-only now)
  const regionLabel = symptom.region && symptom.region !== "head"
    ? symptom.region.replace(/-/g, " ")
    : symptom.side ?? null;

  const unchanged = stagedSeverity === symptom.currentSeverity;

  // ── Compact density (D-SF-R3 Option 3): one row ─────────────────────────────
  if (density === "compact") {
    return (
      <div
        className={
          "flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors " +
          (isDue
            ? "border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-950/30"
            : "border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30")
        }
        title={regionLabel ? `${displayName} · ${regionLabel}` : displayName}
        aria-label={`${displayName}, updated ${relativeTime(symptom.lastUpdatedAt)}`}
      >
        {/* Due-state dot */}
        <span
          className={"h-2 w-2 shrink-0 rounded-full " + (isDue ? "bg-orange-500" : "bg-rose-400")}
          aria-hidden
        />
        <span className="min-w-0 max-w-[8rem] truncate font-medium text-rose-900 dark:text-rose-100">
          {displayName}
        </span>
        <span className="w-7 shrink-0 text-center font-bold tabular-nums text-rose-800 dark:text-rose-100">
          {stagedSeverity}
        </span>
        <span className="shrink-0 text-xs text-rose-400 dark:text-rose-500 tabular-nums">
          {relativeTime(symptom.lastUpdatedAt)}
        </span>
        <div className="min-w-[4rem] flex-1">
          <SeverityBar value={stagedSeverity} onPick={setStagedSeverity} disabled={saving} thin />
        </div>
        <button
          onClick={() => void update(stagedSeverity)}
          disabled={saving}
          title={unchanged ? `Still the same (${stagedSeverity})` : `Update to ${stagedSeverity}`}
          aria-label="Save severity"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-(--accent) text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50 transition-colors"
        >
          ✓
        </button>
        <button
          onClick={() => void update(0, true)}
          disabled={saving}
          title="Mark as resolved — all gone"
          aria-label="Mark resolved"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-rose-300 dark:border-rose-700 text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 disabled:opacity-50 transition-colors"
        >
          ✕
        </button>
        {!hideSnooze && (
          <button
            onClick={() => void snooze()}
            disabled={saving}
            title="Later (snooze)"
            aria-label="Snooze"
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-rose-200 dark:border-rose-800 text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50 transition-colors"
          >
            ⏲
          </button>
        )}
        {error && <span className="shrink-0 text-xs text-red-600 dark:text-red-400">{error}</span>}
      </div>
    );
  }

  // ── Full density (D-SF-R3 Option 1): single control, one action row ─────────
  return (
    <div
      className={
        "rounded-xl border p-3 text-sm shadow-sm transition-colors space-y-2 " +
        (isDue
          ? "border-orange-300 dark:border-orange-600 bg-orange-50 dark:bg-orange-950/30 ring-1 ring-orange-200 dark:ring-orange-700"
          : "border-rose-200 dark:border-rose-800 bg-rose-50 dark:bg-rose-950/30")
      }
    >
      {/* Header: name + chips … value + sparkline */}
      <div className="flex items-center gap-2">
        <div className="min-w-0">
          <span className="font-semibold text-rose-900 dark:text-rose-100 leading-tight">
            {isDue ? "How is it now? " : ""}
            {displayName}
          </span>
          {regionLabel && (
            <span className="ml-1.5 rounded-full bg-rose-100 dark:bg-rose-900/60 px-2 py-0.5 text-xs text-rose-700 dark:text-rose-300">
              {regionLabel}
            </span>
          )}
          <p className="text-xs text-rose-400 dark:text-rose-500 mt-0.5">
            Updated {relativeTime(symptom.lastUpdatedAt)}
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="font-semibold tabular-nums text-rose-800 dark:text-rose-100">
            {stagedSeverity}
            <span className="text-xs font-normal text-rose-500 dark:text-rose-400">/10</span>
          </span>
          {symptom.history.length >= 2 && <Sparkline history={symptom.history} />}
        </div>
      </div>

      {/* Severity tap bar (the single severity control) */}
      <SeverityBar value={stagedSeverity} onPick={setStagedSeverity} disabled={saving} />
      <p className="text-xs text-rose-400 dark:text-rose-500">Tap a level to change</p>

      {/* When: Now / Custom */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex rounded-full bg-rose-100 dark:bg-rose-900/40 p-0.5 text-xs font-medium">
          <button
            onClick={() => setTimeMode("now")}
            className={
              "rounded-full px-3 py-1 transition-all " +
              (timeMode === "now"
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-rose-600 dark:text-rose-400")
            }
          >
            Now
          </button>
          <button
            onClick={selectCustomMode}
            className={
              "rounded-full px-3 py-1 transition-all " +
              (timeMode === "custom"
                ? "bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm"
                : "text-rose-600 dark:text-rose-400")
            }
          >
            Custom
          </button>
        </div>
        {timeMode === "custom" && (
          <>
            <input
              type="date"
              value={customDate}
              max={todayStr()}
              onChange={(e) => setCustomDate(e.target.value)}
              className="rounded-md border border-rose-200 dark:border-rose-800 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
            />
            <input
              type="time"
              value={customTime}
              onChange={(e) => setCustomTime(e.target.value)}
              className="rounded-md border border-rose-200 dark:border-rose-800 bg-white dark:bg-gray-800 px-2 py-1 text-xs text-gray-900 dark:text-gray-100"
            />
          </>
        )}

        {/* Per-symptom reminder interval stepper (D-SF-I5): time, not severity — the
            old severity ▼/▲ stepper (removed in D-SF-R3) is unrelated to this control. */}
        {!hideReminder && (
          <div className="ml-auto flex flex-col items-end gap-0.5">
            <span className="text-[10px] uppercase tracking-wide text-rose-400 dark:text-rose-500">
              Remind me in
            </span>
            <div
              className="flex items-center gap-1"
              title={`Remind me to update this every ${formatReminderInterval(intervalHours)}`}
            >
              <button
                onClick={() => stepInterval(-1)}
                disabled={saving || intervalHours === SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS[0]}
                aria-label="Remind less often"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-rose-200 dark:border-rose-800 text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50 transition-colors"
              >
                ◀
              </button>
              <span className="min-w-[2.5rem] text-center text-xs font-medium tabular-nums text-rose-600 dark:text-rose-400">
                {formatReminderInterval(intervalHours)}
              </span>
              <button
                onClick={() => stepInterval(1)}
                disabled={
                  saving ||
                  intervalHours ===
                    SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS[SYMPTOM_REMINDER_INTERVAL_HOURS_OPTIONS.length - 1]
                }
                aria-label="Remind more often"
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-rose-200 dark:border-rose-800 text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50 transition-colors"
              >
                ▶
              </button>
            </div>
          </div>
        )}
      </div>

      {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}

      {/* Action buttons — single non-wrapping row */}
      <div className="flex gap-2">
        <button
          onClick={() => void update(stagedSeverity)}
          disabled={saving}
          className="flex-1 rounded-lg bg-(--accent) py-2 text-sm font-medium text-(--accent-contrast) hover:bg-(--accent-hover) disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving…" : unchanged ? `Same (${stagedSeverity})` : `Update to ${stagedSeverity}`}
        </button>
        <button
          onClick={() => void update(0, true)}
          disabled={saving}
          className="rounded-lg border border-rose-300 dark:border-rose-700 px-3 py-2 text-sm text-rose-700 dark:text-rose-300 hover:bg-rose-100 dark:hover:bg-rose-900/40 disabled:opacity-50 transition-colors"
          title="Mark as resolved — no longer experiencing this"
        >
          Gone
        </button>
        {!hideSnooze && (
          <button
            onClick={() => void snooze()}
            disabled={saving}
            className="rounded-lg border border-rose-200 dark:border-rose-800 px-3 py-2 text-xs text-rose-500 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-900/20 disabled:opacity-50 transition-colors"
            title="Hide this card for now (snooze)"
          >
            Later
          </button>
        )}
      </div>
    </div>
  );
}
